
import {EventDispatcher} from "../EventDispatcher.js";
import {touchFeature} from "./Feature.js";

// Central store for digitized Feature data (not visuals). Kept intentionally
// small/generic so every digitization tool - present and future - shares the
// same storage, editing and export path instead of inventing its own.
export class FeatureManager extends EventDispatcher {
	constructor(){
		super();

		this.features = [];
		this._byId = new Map();
	}

	add(feature){
		this.features.push(feature);
		this._byId.set(feature.id, feature);
		this.dispatchEvent({type: "feature_added", feature});

		return feature;
	}

	remove(featureId){
		const feature = this._byId.get(featureId);
		if(!feature) return;

		this.features = this.features.filter(f => f.id !== featureId);
		this._byId.delete(featureId);

		this.dispatchEvent({type: "feature_removed", feature});
	}

	get(featureId){
		return this._byId.get(featureId);
	}

	updateGeometry(featureId, coordinates){
		const feature = this._byId.get(featureId);
		if(!feature) return;

		feature.geometry.coordinates = coordinates;
		touchFeature(feature);

		this.dispatchEvent({type: "feature_changed", feature});
	}

	updateProperties(featureId, patch){
		const feature = this._byId.get(featureId);
		if(!feature) return;

		Object.assign(feature.properties, patch);
		touchFeature(feature);

		this.dispatchEvent({type: "feature_changed", feature});
	}

	// Aendrer en registrerings model-/punktsky-tilhoerighed ("Flyt til
	// model...") - en bevidst, eksplicit handling (ikke almindelig mappe-
	// organisering, som aldrig roerer dette felt). Samme moenster som
	// updateProperties(): mutér, touch, dispatch "feature_changed", saa
	// eksisterende lyttere (traeopbygning, Gem-dialog) allerede reagerer
	// korrekt uden en ny event-type.
	setPointcloud(featureId, pointcloudId){
		const feature = this._byId.get(featureId);
		if(!feature) return;

		feature.pointcloudId = pointcloudId != null ? pointcloudId : null;
		touchFeature(feature);

		this.dispatchEvent({type: "feature_changed", feature});
	}

	// Domain-layer counterpart to updateProperties() (DANVA task section 6) -
	// `namespace` is a domain key (e.g. "danva"), never touches `properties`.
	// Dispatches the SAME "feature_changed" event updateProperties() does, so
	// every existing listener (sidebar tree rename/rebuild, FeaturePanel
	// refresh, etc. - see sidebar.js) already reacts correctly without
	// needing a second, DANVA-specific event to be invented and wired
	// everywhere (task section 26/42: "genbrug eksisterende... events").
	setDomainData(featureId, namespace, data){
		const feature = this._byId.get(featureId);
		if(!feature) return;

		feature.domainData[namespace] = data;
		touchFeature(feature);

		this.dispatchEvent({type: "feature_changed", feature});
	}

	// Removes a feature's classification under one domain namespace (e.g.
	// "un-register as DANVA") WITHOUT touching geometry or `properties` -
	// task section 16/40: a feature must survive losing its DANVA
	// classification exactly as it existed before gaining one.
	clearDomainData(featureId, namespace){
		const feature = this._byId.get(featureId);
		if(!feature || !feature.domainData || !(namespace in feature.domainData)) return;

		delete feature.domainData[namespace];
		touchFeature(feature);

		this.dispatchEvent({type: "feature_changed", feature});
	}

	getByCategory(category){
		return this.features.filter(f => f.properties.category === category);
	}

	clear(){
		for(const feature of [...this.features]){
			this.remove(feature.id);
		}
	}
};
